import { FileText, LogOut, Mail, MessageSquare, TrendingDown, TrendingUp, Upload as UploadIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PortalShell } from "../components/PortalShell";
import { PortalMonitoringCard } from "../components/PortalMonitoringCard";
import { ApiError, portal, type PortalClient, type PortalProgress } from "../lib/api";

export default function PortalDashboardPage() {
  const nav = useNavigate();
  const [client, setClient] = useState<PortalClient | null>(null);
  const [progress, setProgress] = useState<PortalProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const c = await portal.me();
        setClient(c);
      } catch (e: any) {
        if (e instanceof ApiError && e.status === 401) {
          nav("/portal/login", { replace: true });
          return;
        }
        setErr(e?.message || "Couldn't load your account.");
        setLoading(false);
        return;
      }
      try {
        const p = await portal.getProgress();
        setProgress(p);
      } catch (e: any) {
        // Non-fatal — show whatever we have.
        setErr(e?.message || null);
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  async function signOut() {
    try {
      await portal.logout();
    } catch {
      // Even if logout fails, drop them at the login screen.
    }
    nav("/portal/login", { replace: true });
  }

  if (loading) {
    return (
      <PortalShell>
        <div className="portal-card">
          <p className="portal-sub on-card">Loading your dashboard…</p>
        </div>
      </PortalShell>
    );
  }

  if (!client) {
    return (
      <PortalShell>
        <div className="portal-card">
          <h2 className="portal-headline on-card" style={{ fontSize: 28 }}>
            We couldn't load your account.
          </h2>
          <p className="portal-sub on-card">{err || "Try signing in again."}</p>
          <Link className="portal-btn-primary wide" to="/portal/login">
            Back to sign-in
          </Link>
        </div>
      </PortalShell>
    );
  }

  const firstName = client.legal_name.split(" ")[0] || "there";
  const scoreStart = progress?.score_start ?? null;
  const scoreCurrent = progress?.score_current ?? null;
  const rounds = progress?.rounds ?? [];
  const letters = (progress?.letters ?? []).slice(0, 5);
  const lettersTotal = (progress?.letters ?? []).length;
  const delta =
    scoreStart !== null && scoreCurrent !== null
      ? scoreCurrent - scoreStart
      : null;

  return (
    <PortalShell
      actions={
        <button onClick={signOut} aria-label="Sign out">
          <LogOut size={14} aria-hidden="true" /> Sign out
        </button>
      }
    >
      {/* Welcome / page title */}
      <section className="portal-section">
        <p className="portal-eyebrow">Your case file</p>
        <h1 className="portal-headline">Welcome back, {firstName}.</h1>
        <p className="portal-sub">
          Here's where things stand on your dispute work. Send us anything you
          receive from the bureaus or creditors and we'll route it to your file.
        </p>
      </section>

      <PortalMonitoringCard />

      {/* 3-card grid: progress / latest letters / documents */}
      <section className="portal-grid-3">
        {/* Card 1 — Your progress */}
        <article className="portal-card">
          <div className="portal-card-heading">
            <h2>Your progress</h2>
          </div>
          <div className="portal-stat-row">
            <div className="portal-stat">
              <span className="label">Start score</span>
              <span className="value">{scoreStart ?? "—"}</span>
            </div>
            <div className="portal-stat">
              <span className="label">Current</span>
              <span className="value">{scoreCurrent ?? "—"}</span>
              {delta !== null && (
                <span
                  className="delta"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    color:
                      delta > 0
                        ? "var(--ne-success)"
                        : delta < 0
                          ? "var(--ne-red-dark)"
                          : "var(--ne-muted)",
                  }}
                >
                  {delta > 0 ? <TrendingUp size={15} aria-hidden="true" /> : delta < 0 ? <TrendingDown size={15} aria-hidden="true" /> : null}
                  {delta > 0 ? `+${delta}` : delta} points
                </span>
              )}
            </div>
          </div>
          <div className="portal-stat-row" style={{ marginBottom: 0 }}>
            <div className="portal-stat">
              <span className="label">Active rounds</span>
              <span className="value" style={{ fontSize: 28 }}>{rounds.length}</span>
            </div>
            <div className="portal-stat">
              <span className="label">Letters sent</span>
              <span className="value" style={{ fontSize: 28 }}>{lettersTotal}</span>
            </div>
          </div>
          {rounds.length === 0 && (
            <p className="portal-sub on-card" style={{ fontSize: 13, marginTop: 4 }}>
              We'll be in touch as soon as Round 1 goes in the mail.
            </p>
          )}
        </article>

        {/* Card 2 — Latest letters */}
        <article className="portal-card">
          <div className="portal-card-heading">
            <h2>Latest letters</h2>
          </div>
          {letters.length === 0 ? (
            <p className="portal-empty">
              <Mail size={15} aria-hidden="true" style={{ verticalAlign: "-2px", marginRight: 6, opacity: 0.6 }} />
              No letters mailed yet.
            </p>
          ) : (
            <div className="portal-list">
              {letters.map((l) => (
                <div className="portal-list-row" key={l.id}>
                  <div>
                    <strong style={{ textTransform: "capitalize" }}>
                      {(l.kind || "").replace(/_/g, " ")}
                    </strong>
                    <div className="meta">
                      {l.bureau || l.recipient || "—"}
                    </div>
                  </div>
                  <span className="meta" style={{ textTransform: "capitalize" }}>
                    {l.status}
                  </span>
                  <span className="right">
                    {new Date(l.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </article>

        {/* Card — Messages */}
        <article className="portal-card">
          <div className="portal-card-heading">
            <h2>Messages{(client.unread_messages ?? 0) > 0 && <span className="tab-badge" style={{ marginLeft: 8 }}>{client.unread_messages}</span>}</h2>
          </div>
          <p className="portal-sub on-card" style={{ fontSize: 13 }}>
            {(client.unread_messages ?? 0) > 0
              ? `You have ${client.unread_messages} new message${client.unread_messages === 1 ? "" : "s"} from your team.`
              : "Questions about your case? Send a message and a specialist will reply here."}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            <Link to="/portal/messages" className={(client.unread_messages ?? 0) > 0 ? "portal-btn-primary wide" : "portal-btn-ghost wide"}>
              <MessageSquare size={16} aria-hidden="true" /> {(client.unread_messages ?? 0) > 0 ? "Read messages" : "Open messages"}
            </Link>
          </div>
        </article>

        {/* Card 3 — Documents */}
        <article className="portal-card">
          <div className="portal-card-heading">
            <h2>Documents</h2>
          </div>
          <p className="portal-sub on-card" style={{ fontSize: 13 }}>
            Got a letter from a bureau, creditor, or collector? Send it over and
            a specialist will review it within one business day.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            <Link to="/portal/upload" className="portal-btn-primary wide">
              <UploadIcon size={16} aria-hidden="true" /> Upload correspondence
            </Link>
            <Link to="/portal/history" className="portal-btn-ghost wide">
              <FileText size={16} aria-hidden="true" /> View upload history
            </Link>
          </div>
        </article>
      </section>

      {err && (
        <p className="portal-sub" style={{ fontSize: 12, opacity: 0.8 }}>
          Note: {err}
        </p>
      )}
    </PortalShell>
  );
}
